/**
 * Marketplace — Service-Katalog im Unified-Cockpit (TQ #6906 Bundle A Phase 3+).
 *
 * 2026-06-02 (Patric): Personal-Mode rendert live die Plan-Liste aus
 * /api/public/personal-catalog. Look ist an den Services.tsx Stil im
 * Org-Cockpit angelehnt (page-Wrapper, services-grid, service-card) damit
 * das Unified-Cockpit überall konsistent aussieht — kein altes
 * "with"-Inline-Style mehr.
 */

import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { isPersonalMode } from '../utils/cockpitMode'
import './Services.css'

interface MarketplaceProps {
  organization: {
    id: number
    name: string
    slug: string
    public_token?: string
  }
}

interface PublicCatalogItem {
  plan_id: string
  item_code: string
  item_name: string
  variant_name?: string
  item_description?: string
  rate: number
  currency: string
  billing_interval: string
  portal_group: string
  portal_category?: string
}

// Org/Staff-Mode Katalog (TQ #6976). Form von GET /api/catalog:
// { services: [{ id, name, category, icon, plans: [...] }] } — eine Service-
// Karte je Item, mit allen Preis-Varianten als Plans darin. Wir klopfen die
// Plans flach (eine Karte je Plan, analog Personal-Mode).
interface CatalogPlan {
  id: number
  erpnext_plan_id: string
  name: string
  item_code?: string | null
  item_name?: string | null
  item_description?: string | null
  description?: string | null
  rate: number
  currency: string
  billing_interval: string
  variant_name?: string | null
}

interface CatalogService {
  id: string
  name: string
  description?: string | null
  features?: string[]
  category?: string
  icon?: string | null
  plans: CatalogPlan[]
}


function formatPrice(rate: number, currency: string, interval: string): string {
  const intervalShort = interval === 'Month' ? '/Monat' : interval === 'Year' ? '/Jahr' : ''
  return `${rate.toFixed(2).replace('.', ',')} ${currency}${intervalShort}`
}

// Per-Item-Code Icon-Mapping. Faellt auf '🧩' zurueck.
const ICON_MAP: Record<string, string> = {
  'PERSONAL-MAIL': '📧',
  'PERSONAL-STORAGE': '☁️',
  'PERSONAL-PASSWORD': '🔐',
  'PERSONAL-STARTER': '🎯',
  'PERSONAL-PLUS': '🚀',
  'PERSONAL-ARIA': '🧠',
}

// Optional: echtes Produkt-Bild statt Emoji-Icon. Liegt unter
// org-portal/public/brand/. Bei vorhandenem Bild rendert die Karte
// stattdessen das <img>.
const IMAGE_MAP: Record<string, string> = {
  'GDATA-IS-1Y':    '/brand/gdata-internet-security-box.png',
  'GDATA-IS-3D-1Y': '/brand/gdata-internet-security-box.png',
}

// Reihenfolge der Sektionen im Personal-Marktplatz (Default-Sortierung).
const CATEGORY_ORDER = ['Persoenlich', 'Sicherheit', 'Workspace']

// Reihenfolge der Sektionen im Org/Staff-Marktplatz (TQ #6976). Deckt sowohl
// die granularen portal_category-Werte (Basis/Kommunikation/…) als auch die
// groben portal_group-Fallbacks (Workspace/MCP …) ab — unbekannte Kategorien
// werden hinten angehaengt (Drift-Resistenz gegen erpnext-sync).
const ORG_CATEGORY_ORDER = [
  'Basis', 'Kommunikation', 'Speicher', 'Produktivität', 'Business',
  'Workspace', 'MCP Add-ons', 'MCP Lizenzen', 'Erweiterungen', 'Sicherheit',
]

// Icon je Item-Code (Org-Mode). Exakte Treffer aus ICON_MAP zuerst, dann
// Prefix-Heuristik, sonst generisches Puzzle-Icon.
function iconFor(code?: string | null): string {
  if (!code) return '🧩'
  const c = code.toUpperCase()
  if (ICON_MAP[c]) return ICON_MAP[c]
  if (c.startsWith('WORKSPACE-MAIL')) return '📧'
  if (c.startsWith('WORKSPACE-STORAGE')) return '☁️'
  if (c.startsWith('WORKSPACE-WEB')) return '🌐'
  if (c.startsWith('WORKSPACE-ERP')) return '📊'
  if (c.startsWith('WORKSPACE-BASE')) return '🏠'
  if (c.startsWith('WORKSPACE')) return '🧰'
  if (c.startsWith('MCP')) return '🔌'
  if (c.startsWith('GDATA')) return '🛡️'
  return '🧩'
}

export default function Marketplace({ organization }: MarketplaceProps) {
  const personalMode = isPersonalMode()
  const { api: keycloakApi, saveSessionForExternalNavigate } = useAuth()
  const [plans, setPlans] = useState<PublicCatalogItem[]>([])
  const [loading, setLoading] = useState(personalMode)
  const [error, setError] = useState<string | null>(null)
  const [bookingPlan, setBookingPlan] = useState<string | null>(null)
  // Org/Staff-Mode Katalog (TQ #6976)
  const [orgServices, setOrgServices] = useState<CatalogService[]>([])
  const [orgLoading, setOrgLoading] = useState(!personalMode)
  const [orgError, setOrgError] = useState<string | null>(null)
  // Aufgeklappte Zeilen der Org-Tabelle (erpnext_plan_id)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  useEffect(() => {
    if (!personalMode) return
    let cancelled = false
    const load = async () => {
      try {
        const resp = await fetch(`https://api.it-sce.de/api/public/personal-catalog?_=${Date.now()}`)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const data = await resp.json()
        if (!cancelled) {
          setPlans(Array.isArray(data.plans) ? data.plans : [])
          setLoading(false)
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? 'Katalog konnte nicht geladen werden.')
          setLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [personalMode])

  // Org/Staff-Mode: Workspace-Katalog laden (TQ #6976). Authentifiziert ueber
  // keycloakApi (User-Token) -> Backend resolved Tier serverseitig
  // (Staff='all' inkl. Personal, Org-Mitglied='business' ohne Personal).
  // Personal-Items werden zusaetzlich im Frontend gefiltert (s.u.).
  useEffect(() => {
    if (personalMode || !keycloakApi) return
    let cancelled = false
    const load = async () => {
      try {
        // tier=workspace ist forward-compat; aktuell tiert das Backend ueber
        // Org-Mitgliedschaft, nicht ueber diesen Query-Param.
        const resp = await keycloakApi.get('/api/catalog', { params: { tier: 'workspace' } })
        if (!cancelled) {
          const svc = Array.isArray(resp.data?.services) ? resp.data.services : []
          setOrgServices(svc)
          setOrgLoading(false)
        }
      } catch (e: any) {
        if (!cancelled) {
          setOrgError(e?.response?.data?.error ?? e?.message ?? 'Katalog konnte nicht geladen werden.')
          setOrgLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [personalMode, keycloakApi])

  const handleBook = async (planId: string) => {
    setBookingPlan(planId)
    if (!keycloakApi) {
      setError('Authentifizierung erforderlich. Bitte neu einloggen.')
      setBookingPlan(null)
      return
    }
    try {
      // TQ #10076: success_url/cancel_url MUST return to the origin the user
      // booked from (window.location.origin) instead of letting the backend
      // fall back to its hardcoded default (https://with.it-sce.de/...).
      //
      // Why this matters — the post-checkout Keycloak session restore
      // (TQ #8883, saveSessionForExternalNavigate below) caches the tokens in
      // sessionStorage, which is scoped PER ORIGIN. A cross-domain redirect
      // (e.g. cockpit.it-sce.de → with.it-sce.de) can't read that cache, so
      // check-sso fails and the user is dumped on the login page — the booking
      // confirmation then never runs. This surfaced most reliably on the
      // 0-euro / 100%-coupon path, where Stripe skips payment collection
      // (PaymentMethodCollection=if_required) and returns immediately.
      //
      // Returning to the same origin keeps sessionStorage readable and the
      // session intact. We deliberately do NOT widen the KC cookie to
      // domain=.it-sce.de — sharing the session cookie across every subdomain
      // is the security downside we avoid here.
      const returnBase = `${window.location.origin}${window.location.pathname}`
      const resp = await keycloakApi.post('/api/checkout/session', {
        plan_id: planId,
        success_url: `${returnBase}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${returnBase}?checkout=cancelled`,
      })
      const url = resp.data?.checkout_url || resp.data?.url
      if (url) {
        saveSessionForExternalNavigate()
        window.location.href = url
      } else {
        setError('Checkout-URL fehlt im Backend-Response.')
        setBookingPlan(null)
      }
    } catch (e: any) {
      setError(`Buchung fehlgeschlagen: ${e?.response?.data?.error ?? e?.message ?? 'Unbekannt'}`)
      setBookingPlan(null)
    }
  }

  // Org/Staff-Mode: Workspace-Katalog inline rendern (TQ #6976). Kein Verweis
  // mehr auf with.it-sce.de — die B2B-Services sind direkt im Cockpit buchbar.
  if (!personalMode) {
    // Services -> Plans flachklopfen (Kategorie + Icon vom Service mitnehmen).
    type FlatPlan = CatalogPlan & { _category: string }
    const flat: FlatPlan[] = []
    for (const svc of orgServices) {
      const cat = svc.category || 'Workspace'
      for (const pl of svc.plans) flat.push({ ...pl, _category: cat })
    }

    // Personal-Items ausblenden: Staff bekommt serverseitig 'all' inkl.
    // Personal -> Frontend muss filtern. Org-Mitglieder bekommen sie schon
    // serverseitig nicht (No-Op-Filter, aber Sicherheitsnetz).
    const isPersonalItem = (p: FlatPlan) =>
      /^PERSONAL[-_]/i.test(p.item_code || '') ||
      p._category === 'Persoenlich' || p._category === 'Personal'
    // Nicht als Angebot zeigen (Patric 03.10.2026): (a) Aria-Token-Top-Up — Aufladung erst mit
    // Preisen aus dem Zielbild Verbrauchsabrechnung (#474), (b) 0-EUR-Plaene (kundenspezifische
    // "Inklusive"-Plaene wie WORKSPACE-*-<KUNDE>-MONTHLY): kein kaufbares Angebot.
    // Preise werden dadurch NICHT veraendert, nur Eintraege ausgeblendet.
    // ACHTUNG: `!(p.rate > 0)` blendet auch Pläne mit fehlendem oder nicht numerischem rate aus,
    // nicht nur 0 EUR. Ein künftiges Gratis-Angebot muss hier freigeschaltet werden.
    const isNotAnOffer = (p: FlatPlan) =>
      /\btop-?up\b/i.test(`${p.item_code ?? ''} ${p.item_name ?? ''} ${p.name ?? ''}`) || !(p.rate > 0)
    const visible = flat.filter((p) => !isPersonalItem(p) && !isNotAnOffer(p))

    // Sicherheits-/Lizenz-Items (G DATA, Antivirus): individuelle Preise ->
    // "Angebot anfragen" statt Direkt-Buchung (gleiches Pattern wie Personal).
    const isSecurity = (p: FlatPlan) =>
      p._category === 'Sicherheit' || /^(GDATA|MCP-AV)[-_]/i.test(p.item_code || '')

    const groups = new Map<string, FlatPlan[]>()
    for (const p of visible) {
      const key = p._category || 'Sonstiges'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(p)
    }
    const orderedKeys = [
      ...ORG_CATEGORY_ORDER.filter((k) => groups.has(k)),
      ...Array.from(groups.keys()).filter((k) => !ORG_CATEGORY_ORDER.includes(k)),
    ]

    return (
      <div className="page">
        <div className="page-header">
          <h1>Marktplatz</h1>
          <p className="page-subtitle">
            Alle Workspace-Services die IT-SCE anbietet — direkt im Cockpit
            buchbar. Bezahlung über Stripe, monatlich kündbar.
          </p>
        </div>

        {orgLoading && (
          <div className="empty-state"><p>Katalog wird geladen …</p></div>
        )}

        {orgError && (
          <div className="empty-state" style={{ borderLeft: '4px solid #ef4444' }}>
            <p style={{ color: '#991b1b' }}>{orgError}</p>
          </div>
        )}

        {error && (
          <div className="empty-state" style={{ borderLeft: '4px solid #ef4444' }}>
            <p style={{ color: '#991b1b' }}>{error}</p>
          </div>
        )}

        {!orgLoading && !orgError && visible.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🛒</div>
            <h2>Service-Katalog</h2>
            <p>Aktuell sind keine Workspace-Services im Katalog verfügbar.</p>
          </div>
        )}

        {/* Tabellarische Darstellung (Patric 02.10.2026): eine Tabelle je Kategorie,
            eine Zeile je Plan, Beschreibung klappt per Pfeil aus. */}
        {!orgLoading && visible.length > 0 && orderedKeys.map((cat) => (
          <section key={cat} style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.1rem', color: '#374151', margin: '0 0 0.75rem' }}>{cat}</h2>
            <table className="market-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: '0.8rem', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ width: 32, padding: '0.5rem' }} aria-label="Details" />
                  <th style={{ padding: '0.5rem' }}>Service</th>
                  <th style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>Preis</th>
                  <th style={{ padding: '0.5rem', width: 150 }} aria-label="Aktion" />
                </tr>
              </thead>
              <tbody>
                {groups.get(cat)!.map((p) => {
                  const security = isSecurity(p)
                  const name = p.item_name || p.name
                  const planId = p.erpnext_plan_id
                  const desc = p.item_description || p.description || ''
                  const open = expanded.has(planId)
                  return (
                    <Fragment key={planId}>
                      <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {desc && (
                            <button
                              onClick={() => toggleExpanded(planId)}
                              aria-expanded={open}
                              aria-label={open ? `${name} einklappen` : `${name} ausklappen`}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', color: '#6b7280' }}
                            >
                              {open ? '▾' : '▸'}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <span aria-hidden style={{ marginRight: '0.5rem' }}>{iconFor(p.item_code)}</span>
                          <strong>{name}</strong>
                          {p.variant_name && p.variant_name !== 'Standard' && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>{p.variant_name}</span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {security ? 'ab ' : ''}{formatPrice(p.rate, p.currency, p.billing_interval)}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                          {security ? (
                            <a
                              href={`mailto:vertrieb@it-sce.de?subject=Angebotsanfrage%20${encodeURIComponent(name)}&body=${encodeURIComponent(
                                `Hallo IT-SCE-Team,\n\nwir interessieren uns für ${name} (${planId}) für die Organisation ${organization.name}.\n\nBitte sendet uns ein passendes Angebot zu.\n\nDanke!`
                              )}`}
                              className="btn-primary"
                              style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem', textDecoration: 'none' }}
                            >
                              Angebot anfragen
                            </a>
                          ) : (
                            <button
                              onClick={() => handleBook(planId)}
                              disabled={bookingPlan === planId}
                              className="btn-primary"
                              style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem', cursor: bookingPlan === planId ? 'wait' : 'pointer', opacity: bookingPlan === planId ? 0.6 : 1 }}
                            >
                              {bookingPlan === planId ? 'Weiterleitung …' : 'Buchen'}
                            </button>
                          )}
                        </td>
                      </tr>
                      {open && desc && (
                        <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
                          <td />
                          <td colSpan={3} style={{ padding: '0.5rem 0.5rem 0.75rem', fontSize: '0.85rem', color: '#4b5563' }}>{desc}</td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </section>
        ))}

        <p className="empty-hint" style={{ marginTop: '2rem' }}>
          Workspace „{organization.name}" — Buchungen werden automatisch dieser
          Organisation zugeordnet.
        </p>
      </div>
    )
  }

  // Personal-Mode: live Plan-Liste im Cockpit-Look (page/services-grid/service-card).
  return (
    <div className="page">
      <div className="page-header">
        <h1>Marktplatz</h1>
        <p className="page-subtitle">
          Persönliche Services — Mailbox, Speicher, Passwort-Manager, KI-Assistentin.
          Bezahlung über Stripe, monatlich kündbar.
        </p>
      </div>

      {loading && (
        <div className="empty-state">
          <p>Katalog wird geladen …</p>
        </div>
      )}

      {error && (
        <div className="empty-state" style={{ borderLeft: '4px solid #ef4444' }}>
          <p style={{ color: '#991b1b' }}>{error}</p>
        </div>
      )}

      {!loading && !error && plans.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h2>Keine Services im Katalog</h2>
          <p>Aktuell sind keine Personal-Pakete verfügbar. Schau später nochmal vorbei.</p>
        </div>
      )}

      {!loading && plans.length > 0 && (() => {
        // Gruppiere nach portal_category (Persoenlich / Sicherheit / Workspace / Sonstiges).
        // TQ #6906 2026-06-03: GDATA wird unabhaengig von portal_category als
        // 'Sicherheit' gegroupt (Drift-Resistenz gegen erpnext-sync).
        const groups = new Map<string, PublicCatalogItem[]>()
        for (const p of plans) {
          let key = p.portal_category || 'Sonstiges'
          if (/^GDATA[-_]/i.test(p.item_code)) key = 'Sicherheit'
          if (!groups.has(key)) groups.set(key, [])
          groups.get(key)!.push(p)
        }
        const orderedKeys = [
          ...CATEGORY_ORDER.filter(k => groups.has(k)),
          ...Array.from(groups.keys()).filter(k => !CATEGORY_ORDER.includes(k)),
        ]
        return orderedKeys.map((cat) => (
          <section key={cat} style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 0 1rem' }}>
              <h2 style={{ fontSize: '1.1rem', color: '#374151', margin: 0 }}>{cat}</h2>
              {cat === 'Sicherheit' && (
                <Link to="/gdata" style={{ fontSize: '0.85rem', color: '#dc2626' }}>
                  Volles G DATA-Lineup →
                </Link>
              )}
            </div>
            <div className="services-grid">
              {groups.get(cat)!.map((p) => (
                <div key={p.plan_id} className="service-card">
              <div className="service-header">
                {IMAGE_MAP[p.item_code] ? (
                  <img
                    src={IMAGE_MAP[p.item_code]}
                    alt={p.item_name}
                    style={{ width: 56, height: 72, objectFit: 'contain', flexShrink: 0 }}
                  />
                ) : (
                  <div className="service-icon" aria-hidden>{ICON_MAP[p.item_code] || '🧩'}</div>
                )}
                <div className="service-info">
                  <div className="service-name">{p.item_name}</div>
                  {p.variant_name && p.variant_name !== 'Standard' && (
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{p.variant_name}</div>
                  )}
                </div>
              </div>

              {p.item_description && (
                <p style={{ fontSize: '0.85rem', color: '#4b5563', flex: 1, margin: '0.5rem 0 1rem' }}>
                  {p.item_description.substring(0, 160)}{p.item_description.length > 160 ? '…' : ''}
                </p>
              )}

              {/* Lizenz-Items (G DATA, Antivirus etc.) haben individuelle Preise
                  pro Geraet/Konstellation → "Angebot anfragen" statt Direkt-Buchung.
                  TQ #6906 2026-06-03: robuster check, weil erpnext-sync portal_category
                  fuer GDATA gelegentlich auf 'Workspace' zurueckdrueckt → wir
                  matchen auch item_code-Prefix (GDATA, MCP-AV) als Sicherheits-
                  Indikator. */}
              {(p.portal_category === 'Sicherheit' || /^GDATA[-_]/i.test(p.item_code)) ? (
                <div style={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                  marginTop: 'auto', paddingTop: '0.75rem',
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    ab {formatPrice(p.rate, p.currency, p.billing_interval)}
                  </div>
                  <a
                    href={`mailto:vertrieb@it-sce.de?subject=Angebotsanfrage%20${encodeURIComponent(p.item_name)}&body=${encodeURIComponent(
                      `Hallo IT-SCE-Team,\n\nich interessiere mich für ${p.item_name} (${p.plan_id}).\n\nBitte sendet mir ein passendes Angebot zu.\n\nDanke!`
                    )}`}
                    className="btn-primary"
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem',
                      textDecoration: 'none',
                    }}
                  >
                    Angebot anfragen
                  </a>
                </div>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                  marginTop: 'auto', paddingTop: '0.75rem',
                }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 600, color: '#111827' }}>
                    {formatPrice(p.rate, p.currency, p.billing_interval)}
                  </div>
                  <button
                    onClick={() => handleBook(p.plan_id)}
                    disabled={bookingPlan === p.plan_id}
                    className="btn-primary"
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.9rem',
                      cursor: bookingPlan === p.plan_id ? 'wait' : 'pointer',
                      opacity: bookingPlan === p.plan_id ? 0.6 : 1,
                    }}
                  >
                    {bookingPlan === p.plan_id ? 'Weiterleitung …' : 'Buchen'}
                  </button>
                </div>
              )}
                </div>
              ))}
            </div>
          </section>
        ))
      })()}

      <p className="empty-hint" style={{ marginTop: '2rem' }}>
        Workspace „{organization.name}" — Buchungen werden deinem Konto zugeordnet.
      </p>
    </div>
  )
}
